/**
 * OperationTypeEditorDrawer — entity-specific drawer для «Тех. операции» (AK-238).
 *
 * Секции (по reference-CRUD эталону AK-231/232/234):
 *   1. Идентификация (код + страна + название RU + описание RU)
 *   2. Классификация (категория — фиксированный enum, типовой срок,
 *      флаги «начинает/закрывает посевное окно» + вид закрытия)
 *   3. Типовые ресурсы (типы техники + входы — free-text теги)
 *   4. Динамическая форма операции (fields_schema) — v1 JSON-редактор
 *   5. Поля работы (work_fields_schema) — v1 JSON-редактор
 *
 * Edit-форма строится из detail-fetch (useOperationType), а не из ListItem —
 * единый паттерн со всеми drawer'ами (PR #290 B1).
 *
 * `code` / `country_code` редактируются только при создании (PATCH их не
 * принимает — это часть UNIQUE-ключа).
 *
 * fields_schema / work_fields_schema — сложные JSONB динамические формы.
 * В v1 редактируем как JSON-текст с клиентской валидацией формата (зеркало
 * validate_fields_schema в backend). Полноценный visual schema-builder —
 * отдельный тикет (см. PR description).
 */
import { useEffect, useState } from 'react';
import { AlertCircle, Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EditorSection,
  ReferenceCatalogDrawer,
} from '@/components/reference/ReferenceCatalogDrawer';
import { NameTranslationsField } from '@/components/reference/NameTranslationsField';
import { useOperationType } from '@/pages/platform-admin/reference/hooks/useCatalogQueries';
import {
  OPERATION_CATEGORIES,
  OPERATION_CLOSE_KINDS,
  type NameTranslations,
  type OperationFieldSchemaItem,
  type OperationTypeCreatePayload,
  type OperationTypeDetail,
  type OperationTypeListItem,
} from '@/pages/platform-admin/reference/types';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';

interface FormState {
  code: string;
  country_code: string;
  name_translations: NameTranslations;
  description_translations: NameTranslations;
  category: string;
  typical_timing: string;
  is_sowing_start: boolean;
  is_sowing_close: boolean;
  close_kind: string;
  typical_machine_types: string[];
  typical_inputs: string[];
  fields_schema_text: string;
  work_fields_schema_text: string;
}

const EMPTY_FORM: FormState = {
  code: '',
  country_code: 'RU',
  name_translations: { ru: '' },
  description_translations: { ru: '' },
  category: '',
  typical_timing: '',
  is_sowing_start: false,
  is_sowing_close: false,
  close_kind: '',
  typical_machine_types: [],
  typical_inputs: [],
  fields_schema_text: '[]',
  work_fields_schema_text: '[]',
};

function schemaToText(schema: OperationFieldSchemaItem[]): string {
  if (!schema || schema.length === 0) return '[]';
  return JSON.stringify(schema, null, 2);
}

function opToForm(o: OperationTypeDetail): FormState {
  return {
    code: o.code,
    country_code: o.country_code,
    name_translations: { ...o.name_translations },
    description_translations: { ...(o.description_translations ?? { ru: '' }) },
    category: o.category ?? '',
    typical_timing: o.typical_timing ?? '',
    is_sowing_start: o.is_sowing_start ?? false,
    is_sowing_close: o.is_sowing_close ?? false,
    close_kind: o.close_kind ?? '',
    typical_machine_types: [...(o.typical_machine_types ?? [])],
    typical_inputs: [...(o.typical_inputs ?? [])],
    fields_schema_text: schemaToText(o.fields_schema),
    work_fields_schema_text: schemaToText(o.work_fields_schema),
  };
}

const ALLOWED_FIELD_TYPES = ['number', 'string', 'enum', 'bool', 'date'];

/**
 * Клиентская валидация JSON-схемы поля. Зеркало validate_fields_schema в
 * reference.py — ловит ошибки до запроса, чтобы не гонять 422 туда-обратно.
 * Возвращает [parsed, error]. parsed=null при любой ошибке.
 */
function parseSchema(
  text: string,
  kind: string,
): [OperationFieldSchemaItem[] | null, string | null] {
  const trimmed = text.trim();
  if (!trimmed) return [[], null];
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    return [null, `${kind}: невалидный JSON — ${(e as Error).message}`];
  }
  if (!Array.isArray(parsed)) return [null, `${kind}: должен быть массивом`];
  const seen = new Set<string>();
  for (let i = 0; i < parsed.length; i += 1) {
    const f = parsed[i] as Record<string, unknown>;
    if (typeof f !== 'object' || f === null)
      return [null, `${kind}[${i}]: должен быть объектом`];
    for (const req of ['key', 'type', 'label'] as const) {
      if (typeof f[req] !== 'string' || !f[req])
        return [null, `${kind}[${i}].${req}: обязателен, непустая строка`];
    }
    if (!ALLOWED_FIELD_TYPES.includes(f.type as string))
      return [
        null,
        `${kind}[${i}].type='${String(f.type)}' не в [${ALLOWED_FIELD_TYPES.join(', ')}]`,
      ];
    if (seen.has(f.key as string))
      return [null, `${kind}: дубль key='${String(f.key)}'`];
    seen.add(f.key as string);
    if (f.type === 'enum') {
      const opts = f.options;
      if (!Array.isArray(opts) || opts.length === 0)
        return [null, `${kind}[${i}]: enum требует непустой options[]`];
    }
    if ('min' in f && 'max' in f && Number(f.min) > Number(f.max))
      return [null, `${kind}[${i}]: min > max`];
  }
  return [parsed as OperationFieldSchemaItem[], null];
}

interface FormErrors {
  code?: string;
  name?: string;
  category?: string;
  fields_schema?: string;
  work_fields_schema?: string;
}

function validate(f: FormState, mode: 'create' | 'edit'): FormErrors {
  const errs: FormErrors = {};
  if (mode === 'create' && !f.code.trim()) errs.code = 'Требуется код';
  const hasName = Object.values(f.name_translations).some(
    (v) => v.trim().length > 0,
  );
  if (!hasName) errs.name = 'Требуется название';
  if (!f.category) errs.category = 'Выберите категорию';
  const [, fsErr] = parseSchema(f.fields_schema_text, 'fields_schema');
  if (fsErr) errs.fields_schema = fsErr;
  const [, wsErr] = parseSchema(f.work_fields_schema_text, 'work_fields_schema');
  if (wsErr) errs.work_fields_schema = wsErr;
  return errs;
}

function toPayload(f: FormState): OperationTypeCreatePayload {
  const trimName: NameTranslations = {};
  for (const [k, v] of Object.entries(f.name_translations))
    if (v.trim()) trimName[k] = v.trim();
  const trimDesc: NameTranslations = {};
  for (const [k, v] of Object.entries(f.description_translations))
    if (v.trim()) trimDesc[k] = v.trim();
  const [fs] = parseSchema(f.fields_schema_text, 'fields_schema');
  const [ws] = parseSchema(f.work_fields_schema_text, 'work_fields_schema');
  return {
    code: f.code.trim(),
    country_code: f.country_code.trim() || 'RU',
    name_translations: trimName,
    description_translations: Object.keys(trimDesc).length ? trimDesc : null,
    category: f.category || null,
    typical_timing: f.typical_timing.trim() || null,
    is_sowing_start: f.is_sowing_start,
    is_sowing_close: f.is_sowing_close,
    close_kind: f.is_sowing_close ? f.close_kind.trim() || null : null,
    typical_machine_types: f.typical_machine_types,
    typical_inputs: f.typical_inputs,
    fields_schema: fs ?? [],
    work_fields_schema: ws ?? [],
  };
}

/** Free-text массив тегов на ui/Input + ui/Badge (Enter / запятая добавляют). */
function TagArrayInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={add}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label="Добавить"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {values.map((v) => (
            <Badge key={v} variant="secondary" className="gap-1 pr-1">
              {v}
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="rounded p-0.5 hover:bg-foreground/10"
                aria-label={`Убрать ${v}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

const schemaTextareaClass =
  'flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ' +
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export interface OperationTypeEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  operationType: OperationTypeListItem | null;
  onSubmit: (payload: OperationTypeCreatePayload) => Promise<void>;
}

export function OperationTypeEditorDrawer({
  open,
  onOpenChange,
  mode,
  operationType,
  onSubmit,
}: OperationTypeEditorDrawerProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // PR #290 B1 — detail-fetch in edit mode.
  const editId =
    open && mode === 'edit' && operationType ? operationType.id : null;
  const detailQ = useOperationType(editId);
  const detailLoading = Boolean(editId) && detailQ.isLoading;

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setForm(EMPTY_FORM);
      setTouched(false);
      return;
    }
    if (detailQ.data) {
      setForm(opToForm(detailQ.data));
      setTouched(false);
    }
  }, [open, mode, detailQ.data]);

  const errors = validate(form, mode);
  const hasErrors = Object.keys(errors).length > 0;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    try {
      await onSubmit(toPayload(form));
      onOpenChange(false);
    } catch (err) {
      // AK-574 — отказ сервера виден пользователю: drawer остаётся открытым,
      // текст 409/422 в тосте. Раньше промах уходил в unhandled rejection.
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const show = (err?: string) => (touched && err ? err : undefined);

  return (
    <ReferenceCatalogDrawer
      open={open}
      onOpenChange={onOpenChange}
      mode={mode}
      entityLabel="тех. операции"
      headerSlot={
        form.name_translations.ru ? (
          <span className="text-sm font-medium text-foreground">
            {form.name_translations.ru}
            {form.code && (
              <span className="ml-2 font-mono text-xs text-muted-foreground">
                {form.code}
              </span>
            )}
          </span>
        ) : undefined
      }
      saveDisabled={detailLoading || (touched && hasErrors)}
      saveLoading={saving}
      onSave={handleSave}
    >
      {detailLoading ? (
        <div className="space-y-4 p-1">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
          <EditorSection number={1} title="Идентификация">
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-sm">
                  Код <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={form.code}
                  onChange={(e) => set('code', e.target.value)}
                  placeholder="seeding"
                  disabled={mode === 'edit'}
                  className={cn(
                    'font-mono',
                    show(errors.code) && 'border-destructive',
                  )}
                />
                {mode === 'edit' && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Код неизменяем после создания.
                  </p>
                )}
                {show(errors.code) && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.code}
                  </p>
                )}
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">Страна</Label>
                <Input
                  value={form.country_code}
                  onChange={(e) =>
                    set('country_code', e.target.value.toUpperCase())
                  }
                  placeholder="RU"
                  disabled={mode === 'edit'}
                  className="font-mono uppercase"
                  maxLength={2}
                />
              </div>
            </div>
            <Label className="mb-1.5 block text-sm">
              Название (RU) <span className="text-destructive">*</span>
            </Label>
            <NameTranslationsField
              value={form.name_translations}
              onChange={(v) => set('name_translations', v)}
              locales={['ru']}
              hint="EN-перевод отложен (отдельный тикет). Сейчас ведём только RU."
              hasError={Boolean(show(errors.name))}
            />
            <Label className="mb-1.5 mt-4 block text-sm">Описание (RU)</Label>
            <Input
              value={form.description_translations.ru ?? ''}
              onChange={(e) =>
                set('description_translations', {
                  ...form.description_translations,
                  ru: e.target.value,
                })
              }
              placeholder="Краткое описание операции"
            />
          </EditorSection>

          <EditorSection
            number={2}
            title="Классификация"
            hint="фиксированный enum категорий"
          >
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-sm">
                  Категория <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={form.category || undefined}
                  onValueChange={(v) => set('category', v)}
                >
                  <SelectTrigger
                    className={
                      show(errors.category) ? 'border-destructive' : undefined
                    }
                  >
                    <SelectValue placeholder="Категория…" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPERATION_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {show(errors.category) && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.category}
                  </p>
                )}
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">Типовой срок</Label>
                <Input
                  value={form.typical_timing}
                  onChange={(e) => set('typical_timing', e.target.value)}
                  placeholder="напр. «ранняя весна»"
                />
              </div>
            </div>

            <div className="mb-3 flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.is_sowing_start}
                  onCheckedChange={(v) => set('is_sowing_start', v)}
                />
                Начинает посевное окно
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.is_sowing_close}
                  onCheckedChange={(v) => set('is_sowing_close', v)}
                />
                Закрывает посевное окно
              </label>
            </div>

            {form.is_sowing_close && (
              <div>
                <Label className="mb-1.5 block text-sm">Вид закрытия</Label>
                <Select
                  value={form.close_kind || undefined}
                  onValueChange={(v) => set('close_kind', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Вид закрытия…" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPERATION_CLOSE_KINDS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </EditorSection>

          <EditorSection number={3} title="Типовые ресурсы">
            <Label className="mb-1.5 block text-sm">Типы техники</Label>
            <TagArrayInput
              values={form.typical_machine_types}
              onChange={(v) => set('typical_machine_types', v)}
              placeholder="напр. «сеялка» (Enter добавит)"
            />
            <Label className="mb-1.5 mt-4 block text-sm">Типовые входы</Label>
            <TagArrayInput
              values={form.typical_inputs}
              onChange={(v) => set('typical_inputs', v)}
              placeholder="напр. «семена» (Enter добавит)"
            />
          </EditorSection>

          <EditorSection
            number={4}
            title="Динамическая форма операции"
            hint="fields_schema · JSON (v1)"
          >
            <p className="mb-2 text-xs text-muted-foreground">
              Массив полей: каждый элемент — {'{'} key, type
              (number/string/enum/bool/date), label, опц. unit, required, min,
              max, options {'}'}. Visual-конструктор — отдельный тикет.
            </p>
            <textarea
              value={form.fields_schema_text}
              onChange={(e) => set('fields_schema_text', e.target.value)}
              className={cn(
                schemaTextareaClass,
                show(errors.fields_schema) && 'border-destructive',
              )}
              spellCheck={false}
            />
            {show(errors.fields_schema) && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.fields_schema}
              </p>
            )}
          </EditorSection>

          <EditorSection
            number={5}
            title="Поля работы"
            hint="work_fields_schema · JSON (v1)"
          >
            <p className="mb-2 text-xs text-muted-foreground">
              Поля, заполняемые при выполнении работы (тот же формат, что и
              fields_schema).
            </p>
            <textarea
              value={form.work_fields_schema_text}
              onChange={(e) => set('work_fields_schema_text', e.target.value)}
              className={cn(
                schemaTextareaClass,
                show(errors.work_fields_schema) && 'border-destructive',
              )}
              spellCheck={false}
            />
            {show(errors.work_fields_schema) && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.work_fields_schema}
              </p>
            )}
          </EditorSection>
        </>
      )}
    </ReferenceCatalogDrawer>
  );
}

export default OperationTypeEditorDrawer;
